import { defineStore } from "pinia";
import { ref } from "vue";
import { client } from "@/main";
import router from "@/router";
import { useCustomerStore } from "@/store/modules/customer";
import { useAuthStore } from "@/store/modules/auth";
import { useConfigStore } from "@/store/modules/config";
export const useProjectStore = defineStore("projects", () => {
    const projectList = ref([]);
    const selectedProject = ref(null);
    const tagList = ref([]);
    async function getProjectList() {
        const customerStore = useCustomerStore();
        if (!customerStore.selectedCustomer)
            return [];
        try {
            const { data } = await client.path("/projects").method("get").create()({
                sort: "+projectName",
                limit: 1000,
                "customerId[eq]": customerStore.selectedCustomer.customerId
            });
            projectList.value = data.data ?? [];
            return data.data;
        }
        catch (e) {
            console.error(e);
        }
    }
    /**
     * A project persisted in localStorage survives a customer switch, so it has to be checked
     * against the customer that is selected now — otherwise a customer with no projects of its
     * own keeps showing the previous customer's project. The loaded projectList (already
     * filtered by customer) decides whenever it has entries; with an empty list we can only
     * compare the project's own customerId.
     */
    function belongsToSelectedCustomer(project) {
        const customerStore = useCustomerStore();
        const customerId = customerStore.selectedCustomer?.customerId;
        if (projectList.value.length > 0) {
            return projectList.value.some((p) => p.projectId === project.projectId);
        }
        return !customerId || !project.customerId || project.customerId === customerId;
    }
    function autoSetProject() {
        let selectedProjectTmp = null;
        const savedProjectStr = localStorage.getItem("selectedProject");
        if (savedProjectStr) {
            const savedProject = JSON.parse(savedProjectStr);
            if (savedProject && belongsToSelectedCustomer(savedProject)) {
                selectedProjectTmp = savedProject;
            }
        }
        const urlProjectId = router.currentRoute.value.params.projectId;
        if (urlProjectId && urlProjectId !== selectedProjectTmp?.projectId) {
            const newProject = projectList.value.filter((p) => p.projectId === urlProjectId)[0];
            if (newProject) {
                selectedProjectTmp = newProject;
            }
        }
        if (!selectedProjectTmp) {
            const customerStore = useCustomerStore();
            selectedProjectTmp =
                projectList.value.filter((p) => p.customerId === customerStore.selectedCustomer?.customerId)[0] ?? null;
        }
        selectedProject.value = selectedProjectTmp;
        if (selectedProjectTmp) {
            localStorage.setItem("selectedProject", JSON.stringify(selectedProjectTmp));
            const configStore = useConfigStore();
            configStore.setNoProjectVisible({
                noProjectVisible: false,
                noProjectVisiblePageInfo: false
            });
        }
        else {
            localStorage.removeItem("selectedProject");
        }
    }
    function setProjectById(id) {
        selectedProject.value = projectList.value.find((proj) => proj.projectId === id) ?? null;
    }
    function setProject(project) {
        if (!project) {
            localStorage.removeItem("selectedProject");
            selectedProject.value = null;
            return;
        }
        selectedProject.value = project;
        localStorage.setItem("selectedProject", JSON.stringify(selectedProject.value));
        const configStore = useConfigStore();
        configStore.setNoProjectVisible({
            noProjectVisible: false,
            noProjectVisiblePageInfo: false
        });
        const authStore = useAuthStore();
        authStore.getApiUser();
        // dispatch("isMqttDevice")
        // dispatch("isMqttGate")
        /**
         * update projectId in url
         */
        const customerStore = useCustomerStore();
        if (router.currentRoute.value.params.projectId !== selectedProject.value?.projectId) {
            const params = {
                customerId: customerStore.selectedCustomer?.customerId,
                projectId: selectedProject.value?.projectId
            };
            /**
             * The home route carries no :customerId/:projectId segments, so a params-only push is
             * resolved against it and the params are dropped without a trace — the user would sit
             * on the empty welcome page even though a project is now selected.
             */
            if (router.currentRoute.value.name === "home") {
                router.push({ name: "dashboard", params });
            }
            else {
                router.push({ params });
            }
        }
    }
    async function getAllTags(projectId) {
        const { data } = await client.path("/tags/devices").method("get").create()({
            "projectId[eq]": projectId ?? selectedProject.value?.projectId,
            limit: 1000
        });
        tagList.value = data.data ?? [];
        return data.data;
    }
    return {
        projectList,
        selectedProject,
        tagList,
        getProjectList,
        autoSetProject,
        setProject,
        setProjectById,
        getAllTags
    };
});
